import { UploadDetailsPopUpProps } from "./types";
import { CircularProgress, LinearProgress, Typography } from "@mui/material";
import { Box, ButtonBase } from "@mui/material";
import { styles } from "./styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useCallback, useEffect, useState } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export const FileUploadStatusCard: React.FC<UploadDetailsPopUpProps> = ({
  uploadingFilesDetailsList,
  setIsUploading,
  uploadingCardsStatusList,
  setUploadingCardsStatusList,
  isProcessing,
  folderUploadInfo,
}) => {
  const [isDrawerMinimize, setIsDrawerMinimize] = useState<boolean>(false);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [currentBatch, setCurrentBatch] = useState<number>(1);

  const drawerStyle = {
    transition: "max-height 0.5s ease",
    maxHeight: isDrawerMinimize ? (isProcessing ? "100px" : "50px") : "400px",
    overflow: "hidden",
  };

  const handleCloseUploadingCard = useCallback(
    (index: number) => {
      setUploadingCardsStatusList((prevList) => {
        const updatedList = prevList.map((file, i) =>
          i === index ? { ...file, isCardClosed: true } : file
        );

        return updatedList;
      });
    },
    [setUploadingCardsStatusList]
  );

  useEffect(() => {
    const remainingFiles = uploadingCardsStatusList.filter(
      (file) => !file.isCardClosed
    );
    if (remainingFiles.length === 0 && !folderUploadInfo) {
      setIsUploading(false);
    }
  }, [uploadingCardsStatusList, setIsUploading, folderUploadInfo]);

  // Get active files (not closed)
  const activeFiles = uploadingFilesDetailsList.filter((file, index) => {
    const currentFile = uploadingCardsStatusList[index];
    return file.isCardOpen && currentFile && !currentFile.isCardClosed;
  });

  // Calculate total batches
  const totalBatches = Math.ceil(activeFiles.length / 10);

  // Calculate the index range for the current batch
  const startIndex = (currentBatch - 1) * 10;
  const endIndex = startIndex + 10;

  // Get visible files for the current batch
  const currentBatchFiles = activeFiles.slice(startIndex, endIndex);

  // Check if all files in current batch are completed
  const allCurrentBatchFilesCompleted = currentBatchFiles.every(
    (file) => file.uploadProgress === 100
  );

  // Auto-move to next batch when all files in current batch are completed
  useEffect(() => {
    if (allCurrentBatchFilesCompleted && currentBatch < totalBatches) {
      const timer = setTimeout(() => {
        setCurrentBatch(currentBatch + 1);
      }, 1000); // 1 second delay before moving to next batch
      return () => clearTimeout(timer);
    }
  }, [allCurrentBatchFilesCompleted, currentBatch, totalBatches]);

  // Calculate overall progress
  const completedFiles = uploadingFilesDetailsList.filter(
    (file) => file.uploadProgress === 100
  ).length;
  const totalFiles = uploadingFilesDetailsList.length;

  const getFolderBatchItems = () => {
    if (!folderUploadInfo?.folderContents) return [];
    const currentFolderBatch = folderUploadInfo.currentBatch || 1;
    const startFolderIndex = (currentFolderBatch - 1) * 10;
    const endFolderIndex = startFolderIndex + 10;

    // Sort items by progress, so in-progress items appear first
    const sortedItems = [...folderUploadInfo.folderContents].sort((a, b) => {
      if (
        a.progress > 0 &&
        a.progress < 100 &&
        (b.progress === 0 || b.progress === 100)
      )
        return -1;
      if (
        b.progress > 0 &&
        b.progress < 100 &&
        (a.progress === 0 || a.progress === 100)
      )
        return 1;
      return 0;
    });

    return sortedItems.slice(startFolderIndex, endFolderIndex);
  };

  return (
    <>
      <Box
        sx={{
          ...styles.uploadingFilesDetailsListContainer,
          ...(isFullScreen
            ? styles.fullScreenUploadingFilesDetailsListContainer
            : drawerStyle),
        }}
      >
        <Box sx={styles.topBar}>
          {folderUploadInfo ? (
            <Typography>
              Uploading folder : ({folderUploadInfo.uploadedFiles}/
              {folderUploadInfo.totalFiles})
            </Typography>
          ) : (
            <Typography variant="h6" sx={styles.uploadingContainer}>
              Uploading{" "}
              {
                uploadingCardsStatusList.filter((file) => !file.isCardClosed)
                  .length
              }{" "}
              items ({completedFiles}/{totalFiles})
            </Typography>
          )}
          <Box>
            {!isFullScreen && (
              <ButtonBase
                onClick={() => setIsDrawerMinimize(!isDrawerMinimize)}
                sx={styles.buttonBase}
              >
                {isDrawerMinimize ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </ButtonBase>
            )}
            <ButtonBase
              onClick={() => setIsFullScreen((prev) => !prev)}
              sx={styles.buttonBase}
            >
              {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </ButtonBase>
            {!isProcessing && (
              <ButtonBase
                onClick={() => setIsUploading(false)}
                sx={{ padding: "8px", color: "white" }}
              >
                <CloseIcon />
              </ButtonBase>
            )}
          </Box>
        </Box>
        {isProcessing && (
          <Box sx={{ marginLeft: "10px" }}>
            <Typography sx={{ marginBottom: "5px" }}>
              In progress please wait...
            </Typography>
            <LinearProgress />
          </Box>
        )}
        {!isDrawerMinimize && !isProcessing && <Box sx={styles.boxStyle}></Box>}
        {folderUploadInfo && (
          <Box>
            <Typography sx={{ color: "white", marginLeft: "10px" }}>{`${
              folderUploadInfo?.uploadedFiles ?? 0
            } / ${
              folderUploadInfo?.totalFiles ?? 0
            } files uploaded`}</Typography>
          </Box>
        )}

        <Box
          sx={{ ...styles.scrollBox, height: isFullScreen ? "100vh" : "auto" }}
        >
          {folderUploadInfo &&
            getFolderBatchItems().length > 0 &&
            getFolderBatchItems().map((item, index) => (
              <Box key={index} sx={{ display: "block" }}>
                <Box sx={styles.popUpContainer}>
                  <Box sx={styles.progressWrapper}>
                    <Box sx={styles.infoContainer}>
                      <Typography sx={styles.fileNameText} variant="h6">
                        {item.name}
                      </Typography>
                      <Typography variant="h6" sx={styles.progressText}>
                        {Math.round(item.progress)}%
                      </Typography>
                    </Box>
                    <Box sx={styles.progressContainer}>
                      {item.progress === 100 ? (
                        <CheckCircleIcon />
                      ) : (
                        <CircularProgress sx={styles.circularProgress} />
                      )}
                    </Box>
                    <Box sx={styles.progressContainer}>
                      <ButtonBase>
                        <CloseIcon />
                      </ButtonBase>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}

          {!folderUploadInfo &&
            currentBatchFiles.map((file) => {
              const originalIndex = uploadingFilesDetailsList.indexOf(file);
              return (
                <Box key={originalIndex} sx={{ display: "block" }}>
                  <Box sx={styles.popUpContainer}>
                    <Box sx={styles.progressWrapper}>
                      <Box sx={styles.infoContainer}>
                        <Typography
                          sx={{ fontSize: "14px", paddingTop: "5px" }}
                          variant="h6"
                        >
                          {file.name}
                        </Typography>
                        <Typography variant="h6" sx={styles.progressText}>
                          {file.uploadProgress}%
                        </Typography>
                      </Box>
                      <Box sx={styles.progressContainer}>
                        {file.uploadProgress === 100 ? (
                          <CheckCircleIcon />
                        ) : (
                          <CircularProgress
                            sx={{ padding: "6px", marginTop: "5px" }}
                          />
                        )}
                      </Box>
                      <Box sx={styles.progressContainer}>
                        <ButtonBase>
                          <CloseIcon
                            onClick={() =>
                              handleCloseUploadingCard(originalIndex)
                            }
                          />
                        </ButtonBase>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>
    </>
  );
};
